import { useQuery } from '@tanstack/react-query';

import { fetchMcpDiscovery } from './discovery';
import { myAiToolsQuery } from './myAiTools';
import { editorAccessKeys } from './queryKeys';

/**
 * What a hint around the app offers (spec 248): nothing, a way to set an AI
 * tool up, or the ask to copy into one.
 */
export type AiToolHelp = 'hidden' | 'setup' | 'copy';

/** Neither answer changes while a page is open; a reload reads both again. */
const STALE_MS = 5 * 60 * 1000;

/**
 * Which hint to show, from whether MCP is on here and whether the person has a
 * tool connected.
 *
 * Nothing until MCP is known to be on: a link to a feature switched off would
 * be wrong. Once it is, the copy unless the tools are known to be none: telling
 * a connected person to set a tool up is worse than offering an ask to someone
 * who has none. Disconnect refreshes the tools, so the hints follow it.
 */
export function useAiToolHelp({
  enabled = true,
}: {
  /**
   * Whether to ask at all. The command palette is mounted on every page, so it
   * asks only once it is open: a host that is always there must not add a
   * request to every page load (its FR-024, as `useWorkspaceList` there).
   */
  enabled?: boolean;
} = {}): AiToolHelp {
  const discovery = useQuery({
    queryKey: editorAccessKeys.quietDiscovery(),
    queryFn: ({ signal }) => fetchMcpDiscovery(signal, { report: false }),
    enabled,
    staleTime: STALE_MS,
    retry: false,
  });
  const on = discovery.data?.status === 'on';
  const tools = useQuery({
    ...myAiToolsQuery,
    enabled: on,
    staleTime: STALE_MS,
    retry: false,
  });
  if (!on) return 'hidden';
  return tools.data?.items.length === 0 ? 'setup' : 'copy';
}
